import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
  ViewEncapsulation,
  HostListener,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { VehicleCardComponent } from '../../../vehicle-card/vehicle-card.component';
import { NotificationService } from '../../../../services/notification.service';
import { Subject, of } from 'rxjs';
import { takeUntil, switchMap, map } from 'rxjs/operators';
import { Order } from '../../../../orders/models/order.models';
import { OrderUtils, isPlateEditableForOrderStatus } from '../../../../orders/utils/order.utils';
import { OrdersService } from '../../../../orders/services/orders.service';
import { VehicleService } from '../../../../services/vehicle.service';
import { ApiService } from '../../../../services/api.service';
import { LoggerService } from '../../../../services/logger.service';
import { FamilyPlanVehicle } from '../../../../shared/types';
import { MANUAL_ENTRY_MESSAGE } from '../../../../shared/utils/vehicle-fetch.util';
import { vendorPlateSyncWarning } from '../../../../shared/utils/vendor-plate-sync-warning.util';
import { activeMembershipPlateWarning } from '../../../../shared/utils/active-membership-plate-warning.util';
import { US_STATES } from '../../../../shared/constants/us-states.constant';
import { OrderDetailsStateService } from '../../services/order-details-state.service';
import { AuthService } from '../../../../services/auth.service';
import { RoleService } from '../../../../services/role.service';
import { SelectSearchComponent } from '../../../../shared/components/select-search/select-search.component';
import { SelectFilterPipe } from '../../../../shared/pipes/select-filter.pipe';

/**
 * Tab component for viewing, editing, fetching, and managing vehicle details within the order details modal, including family plan vehicle selection.
 * @component
 * @selector app-vehicle-details-tab
 * @example
 *   <app-vehicle-details-tab [order]="sample_value1" (vehicleUpdated)="handler()" (tabRequest)="handler()" (closeModal)="handler()"></app-vehicle-details-tab>
 * @input { Order | null } order - The current order whose vehicle details are displayed and edited.
 * @output { EventEmitter<void> } vehicleUpdated - Emitted when a vehicle save changes the underlying order.
 * @output { EventEmitter<string> } tabRequest - Emitted when navigation to another tab is requested, such as returning to the order summary.
 * @output { EventEmitter<void> } closeModal - Emitted when the family plan flow requests the modal to close.
 * @method onEditVehicle - Enters vehicle edit mode and initializes the edit form from the current order.
 * @method onFetchVehicleDetails - Fetches vehicle details by state and license plate and populates the form.
 * @method onSaveVehicleEdit - Saves either a regular order vehicle edit or a family plan vehicle edit.
 * @method onDeleteVehicle - Prompts the user to confirm deletion of the current vehicle.
 * @method tryLoadFamilyPlanVehicles - Loads available family plan vehicles for the order customer.
 * @method onEditFamilyVehicle - Opens edit mode for a selected family plan vehicle.
 * @method onCancelFamilyPlan - Emits a close request for the modal.
 * @method onBackFamilyPlan - Requests navigation back to the order-summary tab.
 * @hook OnDestroy Cleans up internal subscriptions via destroy$ when the component is destroyed.
 */
@Component({
  selector: 'app-vehicle-details-tab',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatSelectModule,
    VehicleCardComponent,
    SelectSearchComponent,
    SelectFilterPipe,
  ],
  templateUrl: './vehicle-details-tab.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  encapsulation: ViewEncapsulation.None,
})
export class VehicleDetailsTabComponent implements OnDestroy {
  @Input() order: Order | null = null;
  /** Emitted when a vehicle save causes the order to change. */
  @Output() vehicleUpdated = new EventEmitter<void>();
  /** Emitted when the user clicks Back (family plan) → go to order-summary tab. */
  @Output() tabRequest = new EventEmitter<string>();
  /** Emitted when the family plan Cancel button is clicked → close the modal. */
  @Output() closeModal = new EventEmitter<void>();

  readonly states = US_STATES;

  private readonly destroy$ = new Subject<void>();

  constructor(
    readonly state: OrderDetailsStateService,
    private readonly vehicleService: VehicleService,
    private readonly ordersService: OrdersService,
    private readonly apiService: ApiService,
    private readonly messageService: NotificationService,
    private readonly logger: LoggerService,
    private readonly authService: AuthService,
    private readonly roleService: RoleService
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Getters ────────────────────────────────────────────────────────────────

  get canFetchVehicle(): boolean {
    return Boolean(this.state.vehicleForm.state && this.state.vehicleForm.licensePlate);
  }

  /**
   * CWS-1384: state and plate are the whole requirement for correcting a plate.
   *
   * This used to also demand a successful lookup or a typed Year/Make/Model, which
   * made a one-character typo fix impossible on a car whose details were already
   * known — and impossible to recover from at all when the details failed to load,
   * since the form blanked and Save locked with nothing to type into it. Year, Make
   * and Model stay editable and are sent only when filled in, so an untouched vehicle
   * keeps the details it already has.
   */
  get isSaveDisabled(): boolean {
    return !this.state.vehicleForm.state?.trim() || !this.state.vehicleForm.licensePlate?.trim();
  }

  // ── Template helpers ───────────────────────────────────────────────────────

  getVehicleDisplayNameForOrder(): string {
    if (!this.order) return '';
    if (this.order.vehicleInfo !== null && this.order.vehicleInfo !== '') {
      return this.order.vehicleInfo
        .split(/\s+/)
        .filter(part => part.toLowerCase() !== 'null' && part.trim() !== '')
        .join(' ')
        .trim();
    }
    return '';
  }

  getVehicleName(): string {
    return this.order ? OrderUtils.getVehicleName(this.order) : 'Unknown Vehicle';
  }

  getVehicleDisplayName(vehicle: FamilyPlanVehicle): string {
    const year = vehicle.year ? String(vehicle.year) : '';
    const make = vehicle.make || '';
    const model = vehicle.model || '';
    return [year, make, model].filter(Boolean).join(' ').trim();
  }

  // ── Mobile menu ────────────────────────────────────────────────────────────

  toggleVehicleMobileMenu(event: Event): void {
    event.stopPropagation();
    this.state.showVehicleMobileMenu = !this.state.showVehicleMobileMenu;
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.state.showVehicleMobileMenu = false;
  }

  // ── Vehicle edit mode ──────────────────────────────────────────────────────

  onVehicleEdit(): void {
    this.onEditVehicle();
  }

  /**
   * Vendor sync notice for a plate edit on an imported customer (CWS-1384), or null
   * for a Way customer. Informational only — the save is never blocked on it.
   */
  get vendorPlateSyncNotice(): string | null {
    return vendorPlateSyncWarning(this.order?.customerSource);
  }

  /**
   * Warning that this car carries a live membership which will follow the plate
   * (CWS-1384). Informational only — the save is never blocked on it.
   */
  get activeMembershipNotice(): string | null {
    return activeMembershipPlateWarning(this.state.vehicleActiveMemberships);
  }

  /**
   * Ask whether this car carries a live membership, so the form can say the plate change
   * will move it. Best-effort: a failure leaves the warning off rather than blocking the
   * edit, because the edit is legitimate either way and the server still decides.
   */
  private loadActiveMembershipsForWarning(): void {
    const vehicleId = this.order?.vehicleId;
    if (!vehicleId) {
      return;
    }
    this.vehicleService
      .getActiveMemberships(vehicleId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.state.vehicleActiveMemberships = response?.data?.memberships ?? [];
        },
        error: () => {
          this.state.vehicleActiveMemberships = [];
        },
      });
  }

  canEditVehicle(): boolean {
    if (!this.order) return false;

    // CWS-1384: the order's own state decides this before any role does. A canceled or
    // in-progress order is not something an admin should be correcting the plate on
    // either, so this guard sits above the role checks rather than beside them.
    if (!isPlateEditableForOrderStatus(this.order.status?.value)) {
      return false;
    }

    if (this.roleService.isAdmin() || this.roleService.isSuperAdmin()) {
      return true;
    }

    if (this.roleService.isShopFloorEmployee()) {
      const currentUser = this.authService.user();
      if (currentUser?.id != null && this.order.userId != null) {
        return String(currentUser.id) === String(this.order.userId);
      }
      return false;
    }

    return true;
  }

  /**
   * Puts the vehicle details form into edit mode and, if an order ID exists, fetches and populates the current vehicle details from the API.
   * @example
   * nEditVehicle()
   * undefined
   * @returns {void} This function does not return a value.
   **/
  onEditVehicle(): void {
    if (!this.canEditVehicle()) return;
    this.state.isVehicleEditMode = true;
    const detectedState = this.detectStateFromLicensePlate(this.order?.licensePlate || '');
    this.state.vehicleForm = {
      state: detectedState,
      licensePlate: this.order?.licensePlate || '',
      vehicle: '',
      year: '',
      make: '',
      model: '',
    };
    this.state.isLoadingVehicleDetails = true;
    this.state.vehicleFetchSuccess = false;
    this.state.vehicleLookupMessage = '';
    this.state.vehicleDetailsLoadError = '';
    this.state.vehicleActiveMemberships = [];
    this.state.hasAttemptedFetch = false;

    this.loadActiveMembershipsForWarning();

    if (this.order?.id) {
      this.apiService
        .get(`order/${this.order.id}`)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (orderResponse: any) => {
            this.state.isLoadingVehicleDetails = false;
            // GET order/:id answers { data: { order, add_ons, discount } }. Reading `data`
            // alone found no make/model/year and silently left the form blank — with no
            // error to show for it, because the request itself had succeeded (CWS-741).
            // Unwrap the same way OrdersService.getOrderById does.
            const payload = orderResponse?.data ?? orderResponse;
            const apiOrder = payload?.order ?? payload;
            const vehicleYear =
              apiOrder?.year?.toString() || apiOrder?.vehicle_year?.toString() || '';
            const vehicleMake = apiOrder?.make || apiOrder?.vehicle_make || '';
            const vehicleModel = apiOrder?.model || apiOrder?.vehicle_model || '';
            const vehicleNameParts = [vehicleYear, vehicleMake, vehicleModel].filter(Boolean);
            const vehicleName = vehicleNameParts.length > 0 ? vehicleNameParts.join(' ') : '';
            const hasVehicleDetails = Boolean(vehicleYear && vehicleMake && vehicleModel);
            this.state.vehicleFetchSuccess = hasVehicleDetails;
            this.state.hasAttemptedFetch = hasVehicleDetails;
            this.state.vehicleForm = {
              ...this.state.vehicleForm,
              vehicle: vehicleName,
              year: vehicleYear,
              make: vehicleMake,
              model: vehicleModel,
            };
          },
          error: () => {
            this.state.isLoadingVehicleDetails = false;
            this.state.vehicleFetchSuccess = false;
            this.state.vehicleLookupMessage = '';
            this.state.hasAttemptedFetch = false;
            // The plate and state are already on the form from the order, so the edit is
            // still usable — say what is missing rather than leave a blank form.
            this.state.vehicleDetailsLoadError =
              "Couldn't load this vehicle's saved details. You can still correct the plate, or type the Year, Make and Model in.";
          },
        });
    } else {
      this.state.isLoadingVehicleDetails = false;
    }
  }

  onCancelVehicleEdit(): void {
    this.state.isVehicleEditMode = false;
    this.state.vehicleFetchSuccess = false;
    this.state.vehicleLookupMessage = '';
    this.state.vehicleDetailsLoadError = '';
    this.state.vehicleActiveMemberships = [];
    this.state.hasAttemptedFetch = false;
    this.state.editingFamilyVehicle = null;
    this.state.vehicleForm = {
      state: '',
      licensePlate: '',
      vehicle: '',
      year: '',
      make: '',
      model: '',
    };
  }

  onSaveVehicleEdit(): void {
    if (!this.order || this.isSaveDisabled) return;
    if (this.state.editingFamilyVehicle) {
      this.saveFamilyPlanVehicleEdit();
    } else {
      this.saveRegularVehicleEdit();
    }
    this.state.isVehicleEditMode = false;
    this.state.editingFamilyVehicle = null;
  }

  /**
   * Fetches vehicle details using the entered state and license plate, then updates the form with the returned data.
   * @example
   * nFetchVehicleDetails()
   * void
   * @returns {void} No return value.
   **/
  onFetchVehicleDetails(): void {
    if (!this.state.vehicleForm.state || !this.state.vehicleForm.licensePlate) return;

    this.state.isLoadingVehicle = true;
    this.state.hasAttemptedFetch = true;
    this.state.vehicleLookupMessage = '';

    const payload = {
      license_plate: this.state.vehicleForm.licensePlate.trim(),
      state: this.state.vehicleForm.state,
    };

    this.vehicleService.getVehicleData(payload).subscribe({
      next: response => {
        this.state.isLoadingVehicle = false;
        if (response.status && response.data) {
          this.state.vehicleForm = {
            ...this.state.vehicleForm,
            vehicle:
              response.data.make && response.data.model
                ? `${response.data.year} ${response.data.make} ${response.data.model}`
                : this.state.vehicleForm.vehicle,
            year: response.data.year ? String(response.data.year) : this.state.vehicleForm.year,
            make: response.data.make || this.state.vehicleForm.make,
            model: response.data.model || this.state.vehicleForm.model,
            fetchedVehicleId: response.data.id,
            fetchedVehicleCustomerId: response.data.customer_id ?? null,
          };
          this.state.vehicleFetchSuccess = true;
        } else {
          this.state.vehicleForm = {
            ...this.state.vehicleForm,
            vehicle: '',
            year: '',
            make: '',
            model: '',
            fetchedVehicleId: undefined,
            fetchedVehicleCustomerId: undefined,
          };
          this.state.vehicleFetchSuccess = false;
          // AskOtto miss: an inline prompt, not a toast. Year/Make/Model below are editable,
          // so the operator can still save by typing the vehicle in.
          this.state.vehicleLookupMessage = MANUAL_ENTRY_MESSAGE;
        }
      },
      error: error => {
        this.state.isLoadingVehicle = false;
        this.state.vehicleForm = {
          ...this.state.vehicleForm,
          vehicle: '',
          year: '',
          make: '',
          model: '',
          fetchedVehicleId: undefined,
          fetchedVehicleCustomerId: undefined,
        };
        this.state.vehicleFetchSuccess = false;
        this.state.vehicleLookupMessage = '';
        this.messageService.add({
          severity: 'error',
          summary: 'Fetch Failed',
          detail: error.error?.message || 'Failed to fetch vehicle data. Please try again.',
        });
      },
    });
  }

  /**
   * Opens a confirmation dialog to delete the current vehicle from the order.
   * @example
   * nDeleteVehicle()
   * void
   * @returns {void} No return value.
   **/
  onDeleteVehicle(): void {
    if (!this.order) return;
    // TODO: Implement with Material Dialog instead of PrimeNG ConfirmationService
    this.logger.info('Delete vehicle for order:', this.order?.orderId);
  }

  // ── Family plan ────────────────────────────────────────────────────────────

  /**
   * Loads the family plan vehicles for the current order customer if they have not already been loaded.
   * @example
   * ryLoadFamilyPlanVehicles()
   * void
   * @returns {void} No return value.
   **/
  tryLoadFamilyPlanVehicles(): void {
    if (!this.order?.customerId) return;
    if (this.state.familyPlanVehicles.length > 0) return;

    this.vehicleService.getFamilyPlanVehicles(this.order.customerId).subscribe({
      next: res => {
        const vehicles = res?.data ?? [];
        this.state.familyPlanVehicles = Array.isArray(vehicles) ? vehicles : [];
        this.state.selectedVehicleId = this.findMatchingVehicleByLicensePlate();
      },
      error: () => {
        this.state.familyPlanVehicles = [];
      },
    });
  }

  selectVehicle(vehicleId: string): void {
    this.state.selectedVehicleId = vehicleId;
  }

  /**
   * Opens the family vehicle editor and populates the form with the selected vehicle's details.
   * @example
   * nEditFamilyVehicle(vehicle, event)
   * undefined
   * @param {FamilyPlanVehicle} vehicle - The family vehicle to edit.
   * @param {Event} [event] - Optional event used to stop propagation when the edit action is triggered.
   * @returns {void} No return value.
   **/
  onEditFamilyVehicle(vehicle: FamilyPlanVehicle, event?: Event): void {
    event?.stopPropagation();
    this.state.editingFamilyVehicle = vehicle;
    this.state.isVehicleEditMode = true;
    this.state.vehicleFetchSuccess = false;
    this.state.vehicleLookupMessage = '';
    this.state.vehicleForm = {
      state: '',
      licensePlate: vehicle.license_plate || '',
      vehicle: this.getVehicleDisplayName(vehicle),
      year: vehicle.year ? String(vehicle.year) : '',
      make: vehicle.make || '',
      model: vehicle.model || '',
    };
  }

  onDeleteFamilyVehicle(vehicle: FamilyPlanVehicle, event?: Event): void {
    event?.stopPropagation();
    this.messageService.add({
      severity: 'info',
      summary: 'Delete Vehicle',
      detail: `${this.getVehicleDisplayName(vehicle)} (coming soon)`,
    });
  }

  onAddVehicle(): void {
    this.messageService.add({
      severity: 'info',
      summary: 'Add Vehicle',
      detail: 'Add vehicle flow coming soon',
    });
  }

  onSelectVehicle(): void {
    if (!this.order?.id || !this.state.selectedVehicleId) return;
    this.ordersService
      .updateOrder(this.order.id, this.state.selectedVehicleId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.refreshOrderVehicleFieldsAndNotify('Vehicle updated for this order.'),
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Update Failed',
            detail: 'Could not update vehicle for this order. Please try again.',
          });
        },
      });
  }

  onCancelFamilyPlan(): void {
    this.closeModal.emit();
  }

  onBackFamilyPlan(): void {
    this.tabRequest.emit('order-summary');
  }

  // ── Private helpers ────────────────────────────────────────────────────────

  /**
   * Saves edits made to a regular vehicle on the order and updates the order state accordingly.
   * @example
   * saveRegularVehicleEdit()
   * void
   * @returns {void} Does not return a value.
   **/
  /**
   * Save a vehicle edit made from an order.
   *
   * Two different intents share this form, and they need different calls (CWS-1384):
   *
   *  - the lookup resolved the plate to a *different* vehicle we already hold, so the
   *    order should point at that one instead. That is an order change.
   *  - no other vehicle was resolved, so the operator is correcting the plate on this
   *    order's own car. That is a vehicle change, and it has to go to the vehicle
   *    endpoint. Until now it did not: the save only re-pointed the order at the same
   *    vehicle id and reported success, so a corrected plate was never written
   *    anywhere and no request to update it was ever made.
   */
  private saveRegularVehicleEdit(): void {
    const { year, make, model, licensePlate, fetchedVehicleId, fetchedVehicleCustomerId } =
      this.state.vehicleForm;
    const nameParts = [year, make, model].filter(Boolean).join(' ').trim();
    this.order!.vehicleInfo = nameParts || this.order!.vehicleInfo;
    this.order!.licensePlate = licensePlate || this.order!.licensePlate;

    const currentVehicleId = this.order!.vehicleId;
    // Only another car of *this order's customer* counts as "a different car". The plate
    // lookup saves every AskOtto hit as a new vehicle with no owner and returns its id;
    // re-pointing the order at that row left the customer's own car on the old plate
    // (CWS-1400). A row with no owner, or one owned by someone else, is treated as a
    // correction of this order's car instead: the vehicle service retires an unowned
    // duplicate so the plate can move, and refuses a plate another customer holds.
    const orderCustomerId = this.order!.customerId;
    const pointsElsewhere = Boolean(
      fetchedVehicleId &&
      fetchedVehicleId !== currentVehicleId &&
      orderCustomerId &&
      fetchedVehicleCustomerId === orderCustomerId
    );

    if (pointsElsewhere) {
      this.updateOrderVehicle(fetchedVehicleId!);
      return;
    }

    if (currentVehicleId) {
      this.updateVehicleFromOrder(currentVehicleId);
      return;
    }

    // CWS-741: this branch used to report success without sending anything at all, so a
    // save against an order with no vehicle silently did nothing.
    this.messageService.add({
      severity: 'error',
      summary: 'Update Failed',
      detail: 'No vehicle is linked to this order, so the details could not be saved.',
    });
  }

  /**
   * Write the edited plate onto the order's own vehicle.
   *
   * Year, Make and Model are sent only when the operator actually has them. The model
   * layer updates just the keys it is given, so omitting a blank field leaves the
   * stored value alone rather than wiping a vehicle whose details simply failed to load
   * (CWS-1384). `vin` is deliberately not sent for the same reason: it is not on this
   * form, and omitting it cannot blank the one the vehicle already has (CWS-741).
   */
  private updateVehicleFromOrder(vehicleId: string): void {
    const { year, make, model, licensePlate, state } = this.state.vehicleForm;

    const payload: Record<string, string> = {
      // Letters and digits only: the shape every plate lookup compares against.
      license_plate: licensePlate.replace(/[^a-z0-9]/gi, '').toUpperCase(),
    };
    if (state?.trim()) payload['state'] = state.trim();
    if (year?.toString().trim()) payload['year'] = year.toString().trim();
    if (make?.trim()) payload['make'] = make.trim();
    if (model?.trim()) payload['model'] = model.trim();

    this.vehicleService
      .updateVehicle(vehicleId, payload as never)
      .pipe(
        // The order keeps the plate it was taken with, and the Orders page shows that
        // snapshot rather than the live vehicle plate. A correction made from the order
        // therefore has to be written onto the order too (CWS-1400).
        switchMap(response =>
          response?.status === false
            ? of(response)
            : this.ordersService
                .updateOrderPlateSnapshot(
                  this.order!.id,
                  payload['license_plate'],
                  payload['state']
                )
                .pipe(map(() => response))
        ),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: response => {
          if (response?.status === false) {
            this.messageService.add({
              severity: 'error',
              summary: 'Update Failed',
              detail: response.message || 'Failed to update vehicle details. Please try again.',
            });
            return;
          }
          this.refreshOrderVehicleFieldsAndNotify(
            'Vehicle details have been updated successfully.'
          );
        },
        error: error => {
          // A plate already held by another car comes back 409 with a usable sentence.
          this.messageService.add({
            severity: 'error',
            summary: 'Update Failed',
            detail: error?.error?.message || 'Failed to update vehicle details. Please try again.',
          });
        },
      });
  }

  /**
   * Updates the vehicle associated with the current order.
   * @example
   * updateOrderVehicle('vehicle-123')
   * void
   * @param {string} vehicleId - The ID of the vehicle to associate with the order.
   * @returns {void} No return value.
   **/
  private updateOrderVehicle(vehicleId: string): void {
    this.ordersService
      .updateOrder(this.order!.id, vehicleId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () =>
          this.refreshOrderVehicleFieldsAndNotify(
            'Vehicle details have been updated successfully.'
          ),
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Update Failed',
            detail: 'Failed to update vehicle details. Please try again.',
          });
        },
      });
  }

  /**
   * Merges fresh vehicle fields from GET order/:id into the bound `order` so VIN / plate / labels
   * update immediately without reloading the orders list.
   */
  private applyVehicleFieldsFromOrder(fresh: Order): void {
    if (!this.order) return;
    this.order.vin = fresh.vin;
    this.order.vinNumber = fresh.vinNumber;
    this.order.licensePlate = fresh.licensePlate;
    this.order.vehicleInfo = fresh.vehicleInfo;
    this.order.vehicleId = fresh.vehicleId;
    if (fresh.isFamilyPlanVehicle !== undefined) {
      this.order.isFamilyPlanVehicle = fresh.isFamilyPlanVehicle;
    }
  }

  private syncFamilyPlanSelectionAfterOrderRefresh(): void {
    if (!this.order || this.state.familyPlanVehicles.length === 0) return;
    const byId = this.order.vehicleId;
    if (byId && this.state.familyPlanVehicles.some(v => v.id === byId)) {
      this.state.selectedVehicleId = byId;
      return;
    }
    const match = this.findMatchingVehicleByLicensePlate();
    if (match) this.state.selectedVehicleId = match;
  }

  private refreshOrderVehicleFieldsAndNotify(detailMessage: string): void {
    if (!this.order?.id) return;
    this.ordersService
      .getOrderById(this.order.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: fresh => {
          this.applyVehicleFieldsFromOrder(fresh);
          this.syncFamilyPlanSelectionAfterOrderRefresh();
          this.vehicleUpdated.emit();
          this.messageService.add({
            severity: 'success',
            summary: 'Vehicle Updated',
            detail: detailMessage,
          });
        },
        error: () => {
          this.messageService.add({
            severity: 'warn',
            summary: 'Updated',
            detail:
              'Vehicle was saved but details could not be refreshed. Reopen this order if something looks wrong.',
          });
          this.vehicleUpdated.emit();
        },
      });
  }

  /**
   * Saves edits to the currently selected family plan vehicle in state.
   * @example
   * saveFamilyPlanVehicleEdit()
   * undefined
   * @returns {void} No return value.
   */
  private saveFamilyPlanVehicleEdit(): void {
    if (!this.state.editingFamilyVehicle) return;
    const { year, make, model, licensePlate } = this.state.vehicleForm;
    const vehicleIndex = this.state.familyPlanVehicles.findIndex(
      v => v.id === this.state.editingFamilyVehicle!.id
    );
    if (vehicleIndex !== -1) {
      this.state.familyPlanVehicles[vehicleIndex] = {
        ...this.state.familyPlanVehicles[vehicleIndex],
        year: year ? parseInt(year, 10) : this.state.familyPlanVehicles[vehicleIndex].year,
        make: make || this.state.familyPlanVehicles[vehicleIndex].make,
        model: model || this.state.familyPlanVehicles[vehicleIndex].model,
        license_plate: licensePlate || this.state.familyPlanVehicles[vehicleIndex].license_plate,
      };
      this.messageService.add({
        severity: 'success',
        summary: 'Vehicle Updated',
        detail: `${this.getVehicleDisplayName(this.state.familyPlanVehicles[vehicleIndex])} has been updated`,
      });
    }
  }

  /**
   * Finds a matching vehicle ID by comparing the order's license plate with family plan vehicles.
   * @example
   * findMatchingVehicleByLicensePlate()
   * "vehicle-id-123"
   * @returns {string | null} The matching vehicle ID if found; otherwise null.
   */
  private findMatchingVehicleByLicensePlate(): string | null {
    if (!this.order || this.state.familyPlanVehicles.length === 0) return null;
    if (this.order.licensePlate) {
      const match = this.state.familyPlanVehicles.find(
        v =>
          v.license_plate &&
          v.license_plate.toLowerCase() === this.order?.licensePlate?.toLowerCase()
      );
      if (match) return match.id;
    }
    return null;
  }

  /**
   * Detects a U.S. state from a license plate string using predefined plate patterns.
   * @example
   * detectStateFromLicensePlate('1ABC123')
   * 'CA'
   * @param {string} licensePlate - The license plate string to evaluate.
   * @returns {string} The inferred state code, or 'CA' as a default fallback.
   **/
  private detectStateFromLicensePlate(licensePlate: string): string {
    if (!licensePlate) return 'CA';
    const statePatterns: Record<string, RegExp> = {
      CA: /^[0-9]{1}[A-Z]{3}[0-9]{3}$/,
      NY: /^[A-Z]{3}[0-9]{4}$/,
      TX: /^[A-Z]{3}[0-9]{4}$/,
      FL: /^[A-Z]{3}[0-9]{3}$/,
      IL: /^[A-Z]{2}[0-9]{5}$/,
    };
    for (const [state, pattern] of Object.entries(statePatterns)) {
      if (pattern.test(licensePlate.toUpperCase())) return state;
    }
    return 'CA';
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
